import React from "react";
import { Bell } from "lucide-react";
import { Button } from "@magicbi/ui-library";
import { VisuallyHidden } from "../../atoms/VisuallyHidden";
import { ToastGlyph } from "../Toast/ToastGlyph";
import { IconBadge } from "../Toast/Toast.styles";
import { Dot, Empty, Entries, Entry, EntryBody, EntryTitle, Head, LinkButton, Meta, Panel, Text, Title } from "./ActivityList.styles";
import type { ActivityListProps } from "./ActivityList.types";

/** What happened lately: each event with when it happened and its one action; new ones carry a dot. */
export const ActivityList: React.FC<ActivityListProps> = ({
  titleId,
  title,
  entries,
  clearLabel,
  onClear,
  onAction,
  newLabel,
  emptyTitle,
  emptyBody,
  className,
}) => (
  <Panel aria-labelledby={titleId} className={className}>
    <Head>
      <Title id={titleId} tabIndex={-1}>
        {title}
      </Title>
      {entries.length ? (
        <Button variant="ghost" size="sm" onClick={onClear}>
          {clearLabel}
        </Button>
      ) : null}
    </Head>
    {entries.length ? (
      <Entries>
        {entries.map((entry) => (
          <Entry key={entry.id} data-kind={entry.kind} data-new={entry.isNew ? "true" : undefined}>
            <IconBadge $kind={entry.kind} aria-hidden="true">
              <ToastGlyph kind={entry.kind} icon={entry.icon} />
            </IconBadge>
            <Text>
              <EntryTitle>
                {entry.isNew ? <VisuallyHidden>{`${newLabel} `}</VisuallyHidden> : null}
                {entry.kindLabel ? <VisuallyHidden>{`${entry.kindLabel}: `}</VisuallyHidden> : null}
                {entry.title}
              </EntryTitle>
              {entry.body ? <EntryBody>{entry.body}</EntryBody> : null}
              <Meta>
                <time dateTime={entry.dateTime} title={entry.timeTitle}>
                  {entry.timeLabel}
                </time>
                {entry.actionLabel ? (
                  <>
                    <span aria-hidden="true">·</span>
                    <LinkButton type="button" onClick={() => onAction(entry.id)}>
                      {entry.actionLabel}
                    </LinkButton>
                  </>
                ) : null}
              </Meta>
            </Text>
            {entry.isNew ? <Dot $error={entry.kind === "error"} aria-hidden="true" /> : <span />}
          </Entry>
        ))}
      </Entries>
    ) : (
      <Empty>
        <Bell aria-hidden="true" />
        <b>{emptyTitle}</b>
        <span>{emptyBody}</span>
      </Empty>
    )}
  </Panel>
);

export default ActivityList;
